import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { compassSubscriptionApi } from '../services/api';
import { useAuth } from '../context/AuthContext';
import {
  CheckCircle2, Lock, ArrowRight, BarChart2, Target, FileText,
  Users, Home, Briefcase, Wallet, ChevronRight,
} from 'lucide-react';

// localStorage key holding: { seen: true, seen_at: ISO, trial_end_date: ISO }
const LS_KEY = 'trial_transition_seen';

// ── LocalStorage helpers ──────────────────────────────────────────────────────

function markSeen(trialEnd) {
  try {
    localStorage.setItem(LS_KEY, JSON.stringify({
      seen: true,
      seen_at: new Date().toISOString(),
      trial_end_date: trialEnd || null,
    }));
  } catch {
    /* storage quota / private mode — nothing we can do */
  }
}

function hasBeenSeen() {
  try {
    const raw = localStorage.getItem(LS_KEY);
    if (!raw) return false;
    const parsed = JSON.parse(raw);
    return parsed?.seen === true;
  } catch {
    return false;
  }
}

// ── Public hook — call from AppRouter after auth ──────────────────────────────

export function useTrialTransitionCheck(user) {
  const navigate = useNavigate();

  useEffect(() => {
    if (!user) return;
    // BETA_MODE: trial transition is meaningless when everyone gets
    // Famille access regardless. Don't open the transition page nor
    // mark anything seen — the user keeps the same UX as a paid user.
    if (user.beta_mode) return;

    const trialEnd = user.trial_end_date;
    if (!trialEnd) return;

    const trialEndDate = new Date(trialEnd);
    const now = new Date();

    // Only show if trial ended (not in trial) and ended within last 30 days
    const isExpired = trialEndDate < now;
    const isRecent = (now - trialEndDate) / (1000 * 60 * 60 * 24) < 30;
    if (!isExpired || !isRecent) return;

    // Don't show again if the user already saw it
    if (hasBeenSeen()) return;

    navigate('/trial-transition');
  }, [user, navigate]);
}

// ── Helpers ───────────────────────────────────────────────────────────────────

function n(val) {
  if (val === undefined || val === null) return '—';
  if (typeof val === 'boolean') return val ? 'Oui' : 'Non';
  return val;
}

function StatCard({ icon: Icon, label, value, color = 'amber' }) {
  const colors = {
    amber:   'text-amber-400 bg-amber-500/10',
    emerald: 'text-emerald-400 bg-emerald-500/10',
    sky:     'text-sky-400 bg-sky-500/10',
    violet:  'text-violet-400 bg-violet-500/10',
    rose:    'text-rose-400 bg-rose-500/10',
  };
  return (
    <div className="flex items-center gap-3 py-2">
      <div className={`w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 ${colors[color] || colors.amber}`}>
        <Icon className="w-4 h-4" />
      </div>
      <span className="text-sm text-zinc-400 flex-1">{label}</span>
      <span className="text-sm font-semibold text-white">{value}</span>
    </div>
  );
}

function FeatureRow({ label, detail, available = true }) {
  return (
    <div className="flex items-start gap-3 py-2">
      <div className="flex-shrink-0 mt-0.5">
        {available
          ? <CheckCircle2 className="w-4 h-4 text-emerald-400" />
          : <Lock className="w-4 h-4 text-zinc-600" />
        }
      </div>
      <div className="flex-1 min-w-0">
        <p className={`text-sm font-medium ${available ? 'text-white' : 'text-zinc-500'}`}>{label}</p>
        {detail && <p className="text-xs text-zinc-500 mt-0.5">{detail}</p>}
      </div>
    </div>
  );
}

// ── Main page ─────────────────────────────────────────────────────────────────

export default function TrialTransition() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);

  const trialEnd = user?.trial_end_date;

  // Mark the screen as seen as soon as it mounts.
  // This handles EVERY exit path — button click, sidebar nav, browser back,
  // URL change — so the hook never redirects here again.
  useEffect(() => {
    if (trialEnd) markSeen(trialEnd);
  }, [trialEnd]);

  // Tab close / browser quit — best-effort flag persist
  useEffect(() => {
    const onLeave = () => { if (trialEnd) markSeen(trialEnd); };
    window.addEventListener('beforeunload', onLeave);
    return () => window.removeEventListener('beforeunload', onLeave);
  }, [trialEnd]);

  // Esc key — same behaviour as "Continuer en gratuit"
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'Escape') {
        markSeen(trialEnd);
        navigate('/dashboard');
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [trialEnd, navigate]);

  // Load usage summary (resources created during the trial)
  useEffect(() => {
    compassSubscriptionApi.getMe()
      .then(res =>
        fetch('/api/subscription/trial-summary', {
          headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
        })
          .then(r => r.json())
          .then(data => ({ me: res.data, summary: data }))
      )
      .then(setSummary)
      .catch(() => setSummary(null))
      .finally(() => setLoading(false));
  }, []);

  const handleContinueFree = () => {
    markSeen(trialEnd);               // idempotent — already marked on mount
    navigate('/dashboard');
  };

  const handleUpgrade = () => {
    markSeen(trialEnd);               // idempotent
    navigate('/pricing?source=trial_transition');
  };

  const res = summary?.summary?.resources_created ?? {};
  const hasHousehold = res.household_created;

  if (loading) {
    return (
      <div className="min-h-screen bg-[#09090B] flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-amber-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#09090B] overflow-y-auto">
      <div className="max-w-lg mx-auto px-6 py-12">

        {/* Header */}
        <div className="text-center mb-10">
          <p className="text-xs text-amber-500 uppercase tracking-widest font-semibold mb-3">Fin de l'essai</p>
          <h1 className="text-2xl font-bold text-white mb-3">14 jours bien utilisés</h1>
          <p className="text-sm text-zinc-400 leading-relaxed">
            Tu passes maintenant sur le plan <strong className="text-white">Gratuit</strong>.
            Tes données restent accessibles à vie.
          </p>
        </div>

        {/* Section 1 — Ce que tu as construit */}
        <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-5 mb-4">
          <div className="flex items-center gap-2 text-xs text-zinc-500 uppercase tracking-widest mb-4">
            <BarChart2 className="w-3.5 h-3.5" />
            Ce que tu as construit en 14 jours
          </div>
          <StatCard icon={Wallet}    label="Cases d'actifs activées" value={n(res.active_patrimony_slots)} color="amber" />
          <StatCard icon={BarChart2} label="Périodes de budget"      value={n(res.budget_periods)}         color="sky" />
          <StatCard icon={Target}    label="Objectifs"                value={n(res.objectives)}             color="emerald" />
          <StatCard icon={FileText}  label="Documents"                value={n(res.documents)}              color="violet" />
          {hasHousehold && (
            <>
              <StatCard icon={Home}      label="Budget ménager (lignes)"     value={n(res.household_budgets)}    color="rose"  />
              <StatCard icon={Briefcase} label="Patrimoine commun (actifs)" value={n(res.shared_patrimonies)}   color="amber" />
            </>
          )}
        </div>

        {/* Section 2 — Ce qui reste actif en gratuit */}
        <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-5 mb-4">
          <div className="flex items-center gap-2 text-xs text-zinc-500 uppercase tracking-widest mb-4">
            <CheckCircle2 className="w-3.5 h-3.5" />
            Ce qui reste actif en gratuit
          </div>
          <FeatureRow label="Toutes tes cases d'actifs" detail="Les cases existantes restent éditables. Création limitée à 2 catégories." available />
          <FeatureRow label="Tes objectifs"            detail="2 objectifs max en création, les existants restent."                 available />
          <FeatureRow label="Tes documents"            detail="Tous téléchargeables. Nouvel upload limité à 5."                     available />
          <FeatureRow label="Simulateur de crédit"     detail="Accessible sans limite."                                              available />
          <FeatureRow label="1 période de budget perso" detail="La période active reste en édition."                                available />
        </div>

        {/* Section 3 — Ce qui passe en lecture seule */}
        {(hasHousehold || (res.active_patrimony_slots > 2)) && (
          <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-5 mb-6">
            <div className="flex items-center gap-2 text-xs text-zinc-500 uppercase tracking-widest mb-4">
              <Lock className="w-3.5 h-3.5" />
              Ce qui passe en lecture seule
            </div>
            {hasHousehold && (
              <>
                <FeatureRow label="Budget ménager"     detail="Consultable, mais plus d'ajout / modification." available={false} />
                <FeatureRow label="Patrimoine commun" detail="Consultable en lecture. Éditions bloquées."      available={false} />
                {res.fictitious_members > 0 && (
                  <FeatureRow label="Membres fictifs" detail="Visibles, modification bloquée." available={false} />
                )}
              </>
            )}
            <FeatureRow label="Export PDF simulateur" detail="Passe à Solo pour réactiver." available={false} />
          </div>
        )}

        {/* CTAs */}
        <div className="flex flex-col gap-3">
          <button
            onClick={handleUpgrade}
            className="w-full flex items-center justify-center gap-2 py-3.5 rounded-xl
                       bg-[#1a2b4a] text-white font-semibold text-sm
                       hover:bg-[#243a63] transition-colors"
          >
            Passer à Solo ou Famille
            <ArrowRight className="w-4 h-4" />
          </button>
          <button
            onClick={handleContinueFree}
            className="w-full flex items-center justify-center gap-2 py-3.5 rounded-xl
                       border border-zinc-700 text-zinc-300 font-medium text-sm
                       hover:bg-zinc-800 transition-colors"
          >
            Continuer en gratuit
          </button>
        </div>

        <p className="text-center text-xs text-zinc-600 mt-5">
          Tes données sont conservées à vie quelle que soit ta décision.
        </p>
      </div>
    </div>
  );
}
